// ============================================================================
// COPY FOR AI — the whole Learning domain onto the clipboard, as markdown.
//
// WHY IT IS HERE. The owner wants to discuss his plans and his test scores with an AI he is
// already paying for — a Claude Code chat on his Max subscription, or any chat window — rather
// than spending LifeOS's runtime API budget on a coach (CLAUDE.md → AI cost policy). The button
// is the door: one press, one paste. `docs/LEARNING-COACH-SESSION.md` is the brief for the chat
// on the other side, and `scripts/learning-export.mts` is the same page from a terminal.
//
// IT NEVER FAKES SUCCESS. "Copied" appears only after `navigator.clipboard.writeText` has
// RESOLVED. A clipboard write can be refused — an insecure origin, a permissions policy, a
// browser that has no clipboard object at all — and a button that flashed "Copied" anyway would
// be the app claiming something it does not know (CLAUDE.md → the app never fakes knowledge).
// When it fails, the failure is NAMED in one line and the markdown is offered as selectable text
// instead, which is honest degradation designed rather than improvised.
//
// TWO PIECES, ONE STATE. The button belongs in the header band and the fallback panel belongs in
// the scrolling column under it — two different parents, so this exports a hook that owns the
// state and two small components that render it. Nothing here derives anything; the page comes
// whole from the data contract.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react'

import { useExportLearning } from '../../lib/data'
import { PlanDoc, Tick } from './icons'

/** How long "Copied" stays up before the button goes back to offering itself. */
const COPIED_MS = 2_000

const NO_CLIPBOARD =
  'This browser would not give the page the clipboard. The text is below — select it and copy.'

type CopyState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'copied' }
  /** `markdown` is null when the READ failed, so there is nothing to offer instead. */
  | { kind: 'failed'; reason: string; markdown: string | null }

export interface CopyForAI {
  state: CopyState
  copy: () => void
  /** Whether the fallback text is currently revealed. */
  shown: boolean
  show: () => void
}

export function useCopyForAI(): CopyForAI {
  const { mutateAsync } = useExportLearning()
  const [state, setState] = useState<CopyState>({ kind: 'idle' })
  const [shown, setShown] = useState(false)
  // A "Copied" that outlives the screen would set state on an unmounted tree; the timer is owned
  // so it can be cleared, and so a second press cannot leave two of them running.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const copy = useCallback(() => {
    setState((current) => (current.kind === 'busy' ? current : { kind: 'busy' }))
    setShown(false)
    if (timer.current) clearTimeout(timer.current)

    void (async () => {
      let markdown: string
      try {
        markdown = await mutateAsync()
      } catch (error) {
        // The Worker's own sentence, not a status code — remote.ts already unwrapped it.
        setState({
          kind: 'failed',
          reason: error instanceof Error ? error.message : 'The export could not be read.',
          markdown: null,
        })
        return
      }

      try {
        // The ONLY thing that earns the word "Copied".
        await navigator.clipboard.writeText(markdown)
      } catch {
        setState({ kind: 'failed', reason: NO_CLIPBOARD, markdown })
        return
      }

      setState({ kind: 'copied' })
      timer.current = setTimeout(() => setState({ kind: 'idle' }), COPIED_MS)
    })()
  }, [mutateAsync])

  return { state, copy, shown, show: () => setShown(true) }
}

/** The band control. Same capsule as the tab pills beside it, so the band stays one row. */
export function CopyForAIButton({ copy }: { copy: CopyForAI }) {
  const { state } = copy
  const busy = state.kind === 'busy'
  const label =
    state.kind === 'busy' ? 'Copying…' : state.kind === 'copied' ? 'Copied' : 'Copy for AI'

  return (
    <button
      type="button"
      onClick={copy.copy}
      disabled={busy}
      // The whole point of the button in one sentence, for the owner who has not read the doc.
      title="Copy every plan, session and note as markdown, to paste into a chat outside LifeOS"
      // Announced, because the label changes under the cursor and a screen reader would otherwise
      // hear nothing happen.
      aria-live="polite"
      className={[
        'mo-press flex flex-none items-center gap-[6px] whitespace-nowrap rounded-pill border px-[11px] py-[6px] text-micro font-medium leading-none',
        busy
          ? 'cursor-wait border-line bg-surface text-muted'
          : state.kind === 'copied'
            ? 'border-success-dot bg-success-fill text-success'
            : 'border-line bg-surface text-ink-3 hover:text-ink',
      ].join(' ')}
    >
      {state.kind === 'copied' ? (
        <Tick size={12} className="text-success" />
      ) : (
        <PlanDoc size={12} className="text-muted" />
      )}
      {label}
    </button>
  )
}

/**
 * What the screen says when the copy did not happen: the reason, in one line, and — when there IS
 * text to offer — a way to get at it by hand. Renders nothing at all the rest of the time.
 */
export function CopyForAIFallback({ copy }: { copy: CopyForAI }) {
  const { state, shown, show } = copy
  if (state.kind !== 'failed') return null

  return (
    <div className="flex-none rounded-context border border-line bg-surface px-5 py-[14px] shadow-card">
      <p className="text-body text-ink-3 text-pretty">{state.reason}</p>
      {state.markdown !== null && !shown && (
        <button
          type="button"
          onClick={show}
          className="mo-press mt-[10px] flex-none rounded-pill border border-line bg-surface px-[11px] py-[6px] text-micro font-medium leading-none text-ink-3 hover:text-ink"
        >
          Show text
        </button>
      )}
      {state.markdown !== null && shown && (
        <textarea
          readOnly
          value={state.markdown}
          aria-label="Learning export, as markdown"
          spellCheck={false}
          className="mt-[10px] h-[260px] w-full resize-y rounded-tile border border-line bg-fill-2 p-[10px] font-mono text-micro leading-[1.5] text-ink-2"
        />
      )}
    </div>
  )
}
